import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import queryString from 'query-string';
import cookie from 'react-cookie';
import config from 'config';
import Grid from 'react-bootstrap/lib/Grid';
import Row from 'react-bootstrap/lib/Row';
import Col from 'react-bootstrap/lib/Col';
import { toPlural, toSeoUrl } from 'helpers/HelperFunctions';

// Components
import BriefFilter from 'components/BriefFilter/BriefFilter';
import BriefListings from 'components/BriefListings/BriefListings';
import ProjectsHeader from './ProjectsHeader/ProjectsHeader';
import SignUp from 'components/SignUp/SignUp';
import Modal from 'components/Modal/Modal';
import UpsellSignUp from 'components/Upsell/UpsellSignUp';
import AriUpsell from 'components/Upsell/AriUpsell';
import JobsList from 'components/JobsList/JobsList';
import ApplicationWriterUpsell from 'components/ApplicationWriterUpsell/ApplicationWriterUpsell';
import PostJobUpsell from 'components/PostJobUpsell/PostJobUpsell';
import { getRoleCategory } from 'helpers/Constants';
import hasUrlFilterParams from './hasUrlFilterParams';

// Constants

const BRIEFS_FILTER_COOKIE_NAME = 'briefsFilters';

export default class Projects extends PureComponent {
  static propTypes = {
    match: PropTypes.object,
    location: PropTypes.object,

    projects: PropTypes.object,
    suggestedRoles: PropTypes.object,
    suggestedSkills: PropTypes.object,

    roleEntities: PropTypes.object,
    skillEntities: PropTypes.object,
    briefsEntities: PropTypes.object,
    userEntities: PropTypes.object,

    loadBriefs: PropTypes.func,
    loadFilterRoleSkills: PropTypes.func,
    loadOwnBriefs: PropTypes.func,
    loadOwnPitches: PropTypes.func,
    loadSuggestedRoles: PropTypes.func,
    loadSuggestedSkills: PropTypes.func,

    posting: PropTypes.bool,
    posted: PropTypes.object,
    postBrief: PropTypes.func,
    clearPostedBrief: PropTypes.func,

    user: PropTypes.object,
    history: PropTypes.object,
    country: PropTypes.string,
    city: PropTypes.string,
    getCitiesForCountry: PropTypes.func,
    cities: PropTypes.object,

    loadingPartners: PropTypes.bool,
    loadedPartners: PropTypes.bool,
    loadPartners: PropTypes.func,
    partners: PropTypes.array,

    setSelectedRoleCategory: PropTypes.func,
    clearSelectedRoleCategory: PropTypes.func,
  };

  // Component Life-cycle
  constructor(props) {
    super(props);

    this.state = {
      filter: {
        name: props.match.params.role ?? '',
        selectedRole: null,
        selectedRoles: props.match.params.role ? [props.match.params.role] : [],
        status: false,
        remote: false,
        internal: false,
        locationText: null,
        country: props.match.params.country ?? null,
        city: props.match.params.city ?? null,
        searchTerm: null,
        experienceLevels: [
          { type: 'Amateur', isChecked: false, label: 'Amateur' },
          { type: 'Beginner', isChecked: false, label: 'Beginner' },
          { type: 'Junior', isChecked: false, label: 'Junior' },
          { type: 'Mid-level', isChecked: false, label: 'Mid-Level' },
          { type: 'Senior', isChecked: false, label: 'Senior' },
          { type: 'Expert', isChecked: false, label: 'Expert' },
        ],
        hiringCapacities: [
          { type: 'personal', isChecked: false, label: 'Personal' },
          { type: 'commercial', isChecked: false, label: 'Commercial' },
        ],
      },
      paid: props.location.pathname.includes('/jobs'),
      showSignUpModal: false,
      experienceLevelsHaveChanged: false,
      hiringCapacitiesHaveChanged: false,
      rolesHaveChanged: false,
      testingPartners: props.location.query.testing ?? false,
    };
  }

  updateCookie = (filters) => {
    const expires = new Date();
    expires.setDate(expires.getDate() + 7); // 7 days
    cookie.save(BRIEFS_FILTER_COOKIE_NAME, filters, { path: '/', expires });
  };

  componentDidMount() {
    // Load Skills and Roles
    this.props.loadSuggestedRoles();
    this.props.loadSuggestedSkills();

    // Window scroll listener - requests more Briefs from API
    document.addEventListener('scroll', this.handleScroll);

    window.history.replaceState(
      { ...window.history.state, cameFromBackButton: false },
      document.title
    );

    const {
      projects,
      loadFilterRoleSkills,
      loadBriefs,
      loadOwnBriefs,
      loadOwnPitches,
      user,
      getCitiesForCountry,
      loadPartners,
      setSelectedRoleCategory,
      clearSelectedRoleCategory,
    } = this.props;

    const {
      paid,
      filter,
      filter: { experienceLevels, hiringCapacities },
      testingPartners,
    } = this.state;

    loadPartners('jobs_board', testingPartners);

    if (
      !projects.getIn(['filterRoleSkills', paid.toString(), 'loaded'], false) &&
      !projects.getIn(['filterRoleSkills', paid.toString(), 'loading'], false)
    ) {
      loadFilterRoleSkills(paid);
    }

    if (filter.selectedRoles && filter.selectedRoles.length > 0) {
      setSelectedRoleCategory(getRoleCategory(filter.selectedRoles[0]));
    } else {
      clearSelectedRoleCategory();
    }

    // Load Briefs
    if (
      !projects.getIn(['briefs', this.getHash(), 'loaded'], false) &&
      !projects.getIn(['briefs', this.getHash(), 'loading'], false)
    ) {
      loadBriefs(
        1,
        paid,
        filter.selectedRoles,
        experienceLevels,
        hiringCapacities,
        filter.status,
        filter.remote,
        filter.internal,
        filter.locationText,
        filter.searchTerm
      );
    }

    if (user && !this.props.match.params.role) {
      // URL filter params (e.g. bookmarks/shared links) take precedence over
      // the saved last-search cookie. Only restore the cookie on bare /jobs.
      if (hasUrlFilterParams(this.props.location.search)) {
        this.updateCookie(this.state.filter);
      } else {
        const filtersFromCookie = cookie.load(BRIEFS_FILTER_COOKIE_NAME);
        if (filtersFromCookie) {
          this.setState({
            filter: filtersFromCookie,
          });
        } else {
          if (!this.state.filter.status) {
            // Check if the user navigated to this page via the back button
            const cameFromBackButton =
              window.history.state && window.history.state.cameFromBackButton;
            if (!cameFromBackButton) {
              this.onStatusFilterToggle();
            }
          }

          if (user.featured_roles.length > 0 && !filter.rolesHaveChanged) {
            this.onRolesSelect(user.featured_roles, true);
          }
          this.updateCookie(this.state.filter);
        }
      }

      // Load Own Briefs
      if (
        !projects.getIn(['ownBriefs', 'loaded'], false) &&
        !projects.getIn(['ownBriefs', 'loading'], false)
      ) {
        loadOwnBriefs(user);
      }

      // Load Own Pitches
      if (
        !projects.getIn(['ownPitches', 'loaded'], false) &&
        !projects.getIn(['ownPitches', 'loading'], false)
      ) {
        loadOwnPitches(user);
      }
    }

    if (filter.country) {
      getCitiesForCountry(filter.country);
      return;
    }
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.handleScroll);
    window.history.replaceState(
      { ...window.history.state, cameFromBackButton: true },
      document.title
    );
  }

  // Filter change listeners
  onSelectedRoleChange = (selectedRole) => {
    const { filter } = this.state;

    filter.type = 'role';
    filter.selectedRole = selectedRole;

    this.setState({ filter });
    this.handleRoleFilterChange();
  };

  onRolesSelect = (selectedRoles, featuredRoles = false) => {
    const { filter } = this.state;

    if (selectedRoles.length === 0 && this.props.match.params.role) {
      filter.name = '';
      filter.selectedRole = null;
    }

    const selectedRolesValues = featuredRoles
      ? selectedRoles
      : selectedRoles.map((item) => item.value);

    filter.selectedRoles = selectedRolesValues;

    this.setState({ filter });
    this.handleFilterChanges();
  };

  onRolesHaveChanged = () => {
    const { filter } = this.state;
    filter.rolesHaveChanged = true;

    this.setState({ filter });
    this.handleFilterChanges();
  };

  onExperienceLevelToggle = (experienceLevel) => {
    const experienceLevels = this.state.filter.experienceLevels;

    const targetIndex = experienceLevels.findIndex(
      (l) => l.type === experienceLevel.type
    );
    experienceLevels[targetIndex].isChecked = !experienceLevel.isChecked;

    this.setState((prevState) => ({
      userData: {
        ...prevState.userData,
        experienceLevels,
      },
      experienceLevelsHaveChanged: true,
    }));
    this.handleFilterChanges();
  };

  onHiringCapacityToggle = (hiringCapacity) => {
    const hiringCapacities = this.state.filter.hiringCapacities;

    const targetIndex = hiringCapacities.findIndex(
      (h) => h.type === hiringCapacity.type
    );
    hiringCapacities[targetIndex].isChecked = !hiringCapacity.isChecked;

    this.setState((prevState) => ({
      userData: {
        ...prevState.userData,
        hiringCapacities,
      },
      hiringCapacitiesHaveChanged: true,
    }));
    this.handleFilterChanges();
  };

  onStatusFilterToggle = () => {
    const { filter } = this.state;
    filter.status = !filter.status;

    this.setState({ filter });
    this.handleFilterChanges();
  };

  onRemoteFilterToggle = () => {
    const { filter } = this.state;
    filter.remote = !filter.remote;

    this.setState({ filter });
    this.handleFilterChanges();
  };

  onInternalFilterToggle = () => {
    const { filter } = this.state;
    filter.internal = !filter.internal;

    this.setState({ filter });
    this.handleFilterChanges();
  };

  onLocationTextChange = (newLocation) => {
    const { filter } = this.state;

    filter.locationText =
      newLocation.city === ''
        ? `${toSeoUrl(newLocation.country)}`
        : `${toSeoUrl(newLocation.city)}, ${toSeoUrl(newLocation.country)}`;

    filter.country = newLocation.country;
    filter.city = newLocation.city;

    this.setState({ filter });
    this.handleFilterChanges();
  };

  onSearchTermChange = (newSearchTerm) => {
    const { filter } = this.state;

    filter.searchTerm = newSearchTerm;

    this.setState({ filter });
    this.handleFilterChanges();
  };

  onFiltersReset = () => {
    const filter = {
      ...this.state.filter,
      name: '',
      selectedRole: null,
      selectedRoles: [],
      status: false,
      remote: false,
      internal: false,
      locationText: null,
      country: null,
      city: null,
      searchTerm: null,
      experienceLevels: [
        { type: 'Amateur', isChecked: false, label: 'Amateur' },
        { type: 'Beginner', isChecked: false, label: 'Beginner' },
        { type: 'Junior', isChecked: false, label: 'Junior' },
        { type: 'Mid-level', isChecked: false, label: 'Mid-Level' },
        { type: 'Senior', isChecked: false, label: 'Senior' },
        { type: 'Expert', isChecked: false, label: 'Expert' },
      ],
      hiringCapacities: [
        { type: 'personal', isChecked: false, label: 'Personal' },
        { type: 'commercial', isChecked: false, label: 'Commercial' },
      ],
    };
    this.setState(
      {
        filter,
      },
      () => {
        this.updateCookie(filter);
        this.props.history.push(`/jobs?rolesHaveChanged=1`);
      }
    );
  };

  // Hash helper function
  getHash = (nextState) => {
    const { paid, filter } = nextState || this.state;

    let rolesString = null;

    if (typeof filter.selectedRoles === 'object') {
      rolesString = filter.selectedRoles.join();
    } else if (typeof filter.selectedRoles === 'string') {
      rolesString = filter.selectedRoles;
    }

    const selectedExperienceLevels = filter.experienceLevels.filter(
      (l) => l.isChecked
    );
    const selectedExperienceLevelsString =
      selectedExperienceLevels.length > 0
        ? selectedExperienceLevels.map((l) => l.type).join()
        : null;

    const selectedHiringCapacities = filter.hiringCapacities.filter(
      (h) => h.isChecked
    );
    const selectedHiringCapacitiesString =
      selectedHiringCapacities.length > 0
        ? selectedHiringCapacities.map((h) => h.type).join()
        : null;

    const status = filter && filter.status ? filter.status : false;
    const remote = filter && filter.remote ? filter.remote : false;
    const internal = filter && filter.internal ? filter.internal : false;

    const jobLocationText =
      filter && filter.locationText ? filter.locationText : null;

    const searchTerm = filter && filter.searchTerm ? filter.searchTerm : null;

    return encodeURIComponent(
      `${paid}|${rolesString}|${selectedExperienceLevelsString}|${selectedHiringCapacitiesString}|${status}|${remote}|${internal}|${jobLocationText}|${searchTerm}`
    );
  };

  getFilterList = (nextState) => {
    const { filter } = nextState || this.state;

    const roles =
      filter.selectedRoles && filter.selectedRoles.length > 0
        ? typeof filter.selectedRoles === 'object'
          ? filter.selectedRoles.join(', ')
          : filter.selectedRoles
        : null;

    const experienceLevels = filter.experienceLevels.filter((l) => l.isChecked);
    const experienceLevelsString =
      experienceLevels.length > 0
        ? experienceLevels.map((l) => l.label).join(', ')
        : null;

    const hiringCapacities = filter.hiringCapacities.filter((h) => h.isChecked);
    const hiringCapacitiesString =
      hiringCapacities.length > 0
        ? hiringCapacities.map((h) => h.label).join(', ')
        : null;

    const status = filter && filter.status ? 'Open Jobs' : false;
    const remote = filter && filter.remote ? 'Remote Jobs' : false;
    const internal = filter && filter.internal ? 'Easy Apply ⚡' : false;
    const jobLocationText =
      filter && filter.locationText
        ? filter.locationText.replaceAll('-', ' ')
        : null;

    const searchTermText =
      filter && filter.searchTerm ? `"${filter.searchTerm}"` : null;

    const filterList = [
      searchTermText,
      roles,
      jobLocationText,
      status,
      remote,
      internal,
      experienceLevelsString,
      hiringCapacitiesString,
    ];

    return filterList.filter(Boolean).join(', ');
  };

  // Redux Getters
  getFilterRoleSkills = () =>
    this.props.projects.getIn(
      ['filterRoleSkills', this.state.paid.toString(), 'roleSkills'],
      List()
    );

  getFilterRoles = () =>
    this.props.projects
      .getIn(['filterRoles', this.state.paid.toString(), 'roles'], List())
      .map((id) => this.props.roleEntities.get(id.toString()).toJS());

  getFilterSkills = () =>
    this.props.projects
      .getIn(['filterSkills', this.state.paid.toString(), 'skills'], List())
      .map((id) => this.props.skillEntities.get(id.toString()).toJS());

  getSuggestedRoles = () =>
    this.props.suggestedRoles
      .get('suggestedRoles', List())
      .map((id) => this.props.roleEntities.get(id.toString()).toJS());

  getSuggestedSkills = () =>
    this.props.suggestedSkills
      .get('suggestedSkills', List())
      .map((id) => this.props.skillEntities.get(id.toString()).toJS());

  // Role Filter
  getCurrentSelectedRole = (filter = null) => {
    let selectedRole = filter || this.state.filter;

    if (filter !== null && typeof filter.slug_plural === 'undefined') {
      // Helper function that updates `selectedRole` if a match is found
      const matchRoleSkill = (filter, roleSkill) => {
        if (
          filter.name.toLowerCase() === roleSkill.name.toLowerCase() ||
          filter.name.toLowerCase() === roleSkill.name_plural.toLowerCase() ||
          filter.name.toLowerCase() === roleSkill.slug.toLowerCase() ||
          filter.name.toLowerCase() === roleSkill.slug_plural.toLowerCase()
        ) {
          selectedRole = roleSkill;
        }
      };

      // Search in FilterRoleSkills first
      this.getFilterRoleSkills()
        .toArray()
        .forEach((roleSkills) => {
          roleSkills.forEach((rs) => matchRoleSkill(filter, rs.toObject()));
        });

      // Then in SuggestedRoles
      this.getSuggestedRoles().forEach((role) => {
        matchRoleSkill(filter, role);
      });

      // Finally, search within the SuggestedSkills
      this.getSuggestedSkills().forEach((skill) => {
        matchRoleSkill(filter, skill);
      });
    }

    return selectedRole;
  };

  getSelectedRoleName = () => {
    const currentSelectedRole = this.getCurrentSelectedRole();

    if (!currentSelectedRole || !currentSelectedRole.name) {
      return '';
    }

    let currentFilterName = currentSelectedRole.name;
    currentFilterName = currentFilterName.replace('-', ' ');
    currentFilterName = currentFilterName.replace('-', ' ');
    currentFilterName = currentFilterName.replace('-', ' ');

    currentFilterName = currentFilterName
      .toLowerCase()
      .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

    currentFilterName = currentFilterName.replace('3d ', '3D ');
    currentFilterName = currentFilterName.replace('Ui ', 'UI ');
    currentFilterName = currentFilterName.replace('Ux ', 'UX ');
    currentFilterName = currentFilterName.replace('Dj', 'DJ');
    currentFilterName = currentFilterName.replace('Front End ', 'Front-end ');
    currentFilterName = currentFilterName.replace('Back End ', 'Back-end ');
    return currentFilterName;
  };

  parseRoles = () => {
    const filteredRoles = [];
    const parsedRoles = queryString.parse(this.props.location.search).roles;

    const { selectedRole } = this.state.filter;

    if (typeof parsedRoles === 'string') {
      filteredRoles.push(parsedRoles);
    } else if (typeof parsedRoles === 'object') {
      parsedRoles.forEach((r) => filteredRoles.push(r));
    }

    if (
      this.props.match.params.role &&
      !filteredRoles.includes(selectedRole.name)
    ) {
      filteredRoles.push(selectedRole.name);
    }

    return filteredRoles ?? [];
  };

  // Parses query string and returns experience levels array
  parseFilteredExperienceLevels = (locationSearch = null) => {
    const search =
      locationSearch !== null ? locationSearch : this.props.location.search;

    // Parse experience levels from query string
    const filteredExperienceLevels = [];
    const parsedExperienceLevels = queryString.parse(search).experienceLevel;

    if (typeof parsedExperienceLevels === 'string') {
      // 1 experience level filtered - represented as string
      filteredExperienceLevels.push(parsedExperienceLevels);
    } else if (typeof parsedExperienceLevels === 'object') {
      // multiple experience levels filtered - represented as string[]
      parsedExperienceLevels.forEach((l) => filteredExperienceLevels.push(l));
    }

    // Update `isChecked` for the experience levels found in the query string
    const stateExperienceLevels = this.state.filter.experienceLevels;

    filteredExperienceLevels.forEach((l) => {
      const parsedExperienceLevel = stateExperienceLevels.find(
        (el) => el.type === l
      );

      if (typeof parsedExperienceLevel !== 'undefined') {
        parsedExperienceLevel.isChecked = true;
      }
    });

    // Experience levels array with updated `isChecked` fields
    return stateExperienceLevels;
  };

  // Parses query string and returns hiring capacities array
  parseFilteredHiringCapacities = (locationSearch = null) => {
    const search =
      locationSearch !== null ? locationSearch : this.props.location.search;

    // Parse hiring capacities from query string
    const filteredHiringCapacities = [];
    const parsedHiringCapacities = queryString.parse(search).hiringCapacity;

    if (typeof parsedHiringCapacities === 'string') {
      // 1 hiring capacity filtered - represented as string
      filteredHiringCapacities.push(parsedHiringCapacities);
    } else if (typeof parsedHiringCapacities === 'object') {
      // multiple hiring capacities filtered - represented as string[]
      parsedHiringCapacities.forEach((h) => filteredHiringCapacities.push(h));
    }

    // Update `isChecked` for the hiring capacities found in the query string
    const stateHiringCapacities = this.state.filter.hiringCapacities;

    filteredHiringCapacities.forEach((h) => {
      const parsedHiringCapacity = stateHiringCapacities.find(
        (hc) => hc.type === h
      );

      if (typeof parsedHiringCapacity !== 'undefined') {
        parsedHiringCapacity.isChecked = true;
      }
    });

    // Hiring Capacities array with updated `isChecked` fields
    return stateHiringCapacities;
  };

  // Parse Job Status Open filter
  parsestatus = (locationSearch = null) => {
    const search =
      locationSearch !== null ? locationSearch : this.props.location.search;

    const { status } = queryString.parse(search);

    return !!status;
  };

  // Parse Roles Have Changed Job filter
  parseRolesHaveChanged = (locationSearch = null) => {
    const search =
      locationSearch !== null ? locationSearch : this.props.location.search;

    const { rolesHaveChanged } = queryString.parse(search);

    return !!rolesHaveChanged;
  };

  // Parse Remote Job filter
  parseRemote = (locationSearch = null) => {
    const search =
      locationSearch !== null ? locationSearch : this.props.location.search;

    const { remote } = queryString.parse(search);

    return !!remote;
  };

  // // Parse easyApply Job filter
  parseInternal = (locationSearch = null) => {
    const search =
      locationSearch !== null ? locationSearch : this.props.location.search;

    const { internal } = queryString.parse(search);

    return !!internal;
  };

  parseSearchTerm = (locationSearch = null) => {
    const search =
      locationSearch !== null ? locationSearch : this.props.location.search;

    const { searchTerm } = queryString.parse(search);

    return searchTerm;
  };

  // Other Handlers
  handleScroll = () => {
    const distanceFromBottom =
      document.getElementById('brief-listings').getBoundingClientRect().bottom -
      window.innerHeight;

    if (distanceFromBottom < 450) {
      this.loadMore();

      const loadCount = this.getPagesLoaded();

      if (loadCount === 2 && !this.props.user) {
        this.setState({
          showSignUpModal: true,
        });
      }
    }
  };

  handleAddBrief = (overview, description, role) =>
    this.props.postBrief(overview, description, role);

  // Filter Change Handler
  handleFilterChanges = () => {
    const { filter, paid } = this.state;
    const experienceLevels = this.state.filter.experienceLevels;
    const hiringCapacities = this.state.filter.hiringCapacities;

    this.updateCookie(filter);

    // Role Filter
    const roleFilter = { name: filter.name, selectedRole: filter.selectedRole };
    // Experience Level Filter
    const experienceLevelFilter = experienceLevels.filter((l) => l.isChecked);
    // Hiring Capacity Filter
    const hiringCapacityFilter = hiringCapacities.filter((h) => h.isChecked);

    // Generate redirection URL
    let newPathname = `/${paid ? 'jobs' : 'collaborate'}`;

    // Role Filter
    if (roleFilter.selectedRole) {
      if (roleFilter.selectedRole.slug_plural === undefined) {
        newPathname += `/${toPlural(
          roleFilter.selectedRole.name.toLowerCase().replace(' ', '-')
        )}`;
      } else {
        newPathname += `/${roleFilter.selectedRole.slug_plural}`;
      }
    }

    if (filter.locationText) {
      newPathname += filter.city
        ? `/in/${toSeoUrl(filter.country)}/${toSeoUrl(filter.city)}`
        : `/in/${toSeoUrl(filter.country)}`;
    }
    // Query string
    const query = {};

    if (filter.selectedRoles.length !== 0) {
      query.roles = filter.selectedRoles;
    }

    // Experience Level Filter
    if (experienceLevelFilter.length !== 0) {
      query.experienceLevel = experienceLevelFilter.map((l) => l.type);
    }

    // Hiring Capacity Filter
    if (hiringCapacityFilter.length !== 0) {
      query.hiringCapacity = hiringCapacityFilter.map((h) => h.type);
    }

    // Job Status Open Filter
    if (filter.status) {
      query.status = true;
    }

    if (filter.internal) {
      query.internal = true;
    }

    // Remote Job Filter
    if (filter.remote) {
      query.remote = 1;
    }

    // Remote Job Filter
    if (filter.rolesHaveChanged) {
      query.rolesHaveChanged = 1;
    }

    if (filter.searchTerm) {
      query.searchTerm = filter.searchTerm;
    }

    this.props.history.push({
      pathname: newPathname,
      search: queryString.stringifyUrl({ url: '', query }),
    });
  };

  // Role Filter Change Handler
  handleRoleFilterChange = (filter = null) => {
    // Get filter role based on current filter
    const currentFilter =
      filter === null ? this.state.filter.selectedRole : filter;
    const currentRoleSkill = this.getCurrentSelectedRole(currentFilter);

    // Update existing Role Filter within state
    const newFilter = this.state.filter;
    newFilter.selectedRole = currentRoleSkill;
    newFilter.name = currentRoleSkill.slug_plural;
    newFilter.type = currentRoleSkill.type;

    this.setState({ filter: newFilter });

    const filterHasChanged =
      (typeof currentRoleSkill.slug_plural !== 'undefined' &&
        this.props.match.params.role &&
        this.props.match.params.role !== currentRoleSkill.slug_plural) ||
      !this.props.match.params.role;

    if (filterHasChanged) {
      this.handleFilterChanges();
    }
  };

  // Redux Loaders
  loadSuggestedRoles = () => {
    if (
      !this.props.suggestedRoles.get('loaded', false) &&
      !this.props.suggestedRoles.get('loading', false)
    ) {
      this.props.loadSuggestedRoles();
    }
  };

  loadSuggestedSkills = () => {
    if (
      !this.props.suggestedSkills.get('loaded', false) &&
      !this.props.suggestedSkills.get('loading', false)
    ) {
      this.props.loadSuggestedSkills();
    }
  };

  getPagesLoaded = () => {
    const hash = this.getHash();

    return this.props.projects.getIn(['briefs', hash, 'pagesLoaded'], 0);
  };

  loadMore = () => {
    const hash = this.getHash();

    if (
      this.props.projects.getIn(['briefs', hash, 'moreToLoad'], false) &&
      !this.props.projects.getIn(['briefs', hash, 'loading'], false)
    ) {
      this.props.loadBriefs(
        this.props.projects.getIn(['briefs', hash, 'pagesLoaded'], 0) + 1,
        this.state.paid,
        this.state.filter.selectedRoles,
        this.state.filter.experienceLevels,
        this.state.filter.hiringCapacities,
        this.state.filter.status,
        this.state.filter.remote,
        this.state.filter.internal,
        this.state.filter.locationText,
        this.state.filter.searchTerm
      );
    }
  };

  UNSAFE_componentWillMount() {
    const { filter } = this.state;

    // Read initial Role Filter, based on URL slug
    const roleSkillParam = this.props.match.params.role;

    const locationParam = this.props.match.params.city
      ? `${this.props.match.params.city}, ${this.props.match.params.country}`
      : this.props.match.params.country;

    if (roleSkillParam) {
      filter.selectedRole = this.getCurrentSelectedRole({
        name: roleSkillParam,
      });
    }

    // Read initial Roles Filters, based on query string
    filter.selectedRoles = this.parseRoles();

    // Read initial Experience Level Filter, based on query string
    const experienceLevels = this.parseFilteredExperienceLevels();

    // Read initial Hiring Capacity Filter, based on query string
    const hiringCapacities = this.parseFilteredHiringCapacities();

    // Read initial Job State Open Filter, based on query string
    filter.status = this.parsestatus();

    // Read initial Job State Open Filter, based on query string
    filter.remote = this.parseRemote();

    // Read initial Roles Have Changed, based on query string
    filter.rolesHaveChanged = this.parseRolesHaveChanged();

    filter.internal = this.parseInternal();

    // Read initial Job Location Text Filter, based on query string
    filter.locationText = locationParam;

    filter.searchTerm = this.parseSearchTerm();

    // Update state with new values for RoleFilter, ExperienceLevelFilter and HiringCapacityFilter
    this.setState({ filter, experienceLevels, hiringCapacities });
  }

  UNSAFE_componentWillUpdate(nextProps, nextState) {
    const newState = {};

    // Update Role Filter, on URL slug change
    if (nextProps.match.params.role !== this.props.match.params.role) {
      if (nextProps.match.params.role) {
        newState.filter = {
          ...nextState.filter,
          name: nextProps.match.params.role,
          selectedRole: this.getCurrentSelectedRole({
            name: nextProps.match.params.role,
          }),
          selectedRoles: [
            this.getCurrentSelectedRole({
              name: nextProps.match.params.role,
            }).name,
          ],
        };
      } else {
        newState.filter = {
          ...nextState.filter,
          name: '',
          selectedRole: null,
          selectedRoles: [],
        };
      }
    }

    // Update Experience Level Filter, if query string changed
    let experienceLevelsChanged = nextState.experienceLevelsHaveChanged;

    // Update Hiring Capacity Filter, if query string changed
    let hiringCapacitiesChanged = nextState.hiringCapacitiesHaveChanged;

    if (this.props.location.search !== nextProps.location.search) {
      experienceLevelsChanged = true;
      hiringCapacitiesChanged = true;
    }

    // Update `paid` if navigated to collaborate or jobs
    if (
      nextProps.location.pathname.includes('/jobs') !==
      this.props.location.pathname.includes('/jobs')
    ) {
      newState.paid = nextProps.location.pathname.includes('/jobs');
    }

    // Load RoleSkills if `paid` changed
    if (nextState.paid !== this.state.paid) {
      if (
        !this.props.projects.getIn(
          ['filterRoleSkills', this.state.paid.toString(), 'loaded'],
          false
        ) &&
        !this.props.projects.getIn(
          ['filterRoleSkills', this.state.paid.toString(), 'loading'],
          false
        )
      ) {
        this.props.loadFilterRoleSkills(this.state.paid);
      }
    }

    // Reload Briefs - if `paid`, `filter`, `experienceLevels` or `hiringCapacities` have changed
    const paidChanged = nextState.paid !== this.state.paid;
    const roleSkillChanged = nextState.filter !== this.state.filter;
    const rolesChanged =
      nextState.filter.selectedRoles !== this.state.filter.selectedRoles;
    const jobStatusFilterChanged =
      nextState.filter.status !== this.state.filter.status;
    const remoteFilterChanged =
      nextState.filter.remote !== this.state.filter.remote;
    const internalFilterChanged =
      nextState.filter.internal !== this.state.filter.internal;
    const jobLocationTextChanged =
      nextState.filter.locationText !== this.state.filter.locationText;
    const jobSearchTermChanged =
      nextState.filter.searchTerm !== this.state.filter.searchTerm;

    if (
      paidChanged ||
      roleSkillChanged ||
      rolesChanged ||
      experienceLevelsChanged ||
      hiringCapacitiesChanged ||
      jobStatusFilterChanged ||
      remoteFilterChanged ||
      internalFilterChanged ||
      jobLocationTextChanged ||
      jobSearchTermChanged
    ) {
      // Reset experienceLevelsHaveChanged
      newState.experienceLevelsChanged = false;

      // Reset hiringCapacitiesHaveChanged
      newState.hiringCapacitiesChanged = false;

      // Decide whether to re-load briefs

      const briefsHaveLoaded = this.props.projects.getIn(
        ['briefs', this.getHash(nextState), 'loaded'],
        false
      );
      const briefsAreLoading = this.props.projects.getIn(
        ['briefs', this.getHash(nextState), 'loading'],
        false
      );

      if (rolesChanged) {
        if (
          nextState.filter.selectedRoles &&
          nextState.filter.selectedRoles.length > 0
        ) {
          this.props.setSelectedRoleCategory(
            getRoleCategory(nextState.filter.selectedRoles[0])
          );
        } else {
          this.props.clearSelectedRoleCategory();
        }
      }

      if (!briefsHaveLoaded && !briefsAreLoading) {
        this.props.loadBriefs(
          this.props.projects.getIn(
            ['briefs', this.getHash(nextState), 'pagesLoaded'],
            0
          ) + 1,
          nextState.paid,
          nextState.filter.selectedRoles,
          nextState.filter.experienceLevels,
          nextState.filter.hiringCapacities,
          nextState.filter.status,
          nextState.filter.remote,
          nextState.filter.internal,
          nextState.filter.locationText,
          nextState.filter.searchTerm
        );
      }
    }

    if (!this.props.posted && nextProps.posted) {
      this.props.history.push(nextProps.posted.links.ats_main_relative);
    }

    // Update State
    if (Object.keys(newState).length !== 0) {
      this.setState(newState);
    }
  }

  render() {
    const {
      projects,
      suggestedRoles,
      briefsEntities,
      userEntities,
      location,
      user,
      cities,
      partners,
      loadedPartners,
      loadingPartners,
    } = this.props;

    const { paid, filter, showSignUpModal } = this.state;

    const showUpsell = !user || (user && user?.pro === false);

    const briefListings = projects
      .getIn(['briefs', this.getHash(), 'briefs'], List())
      .map((brief) => {
        const object = briefsEntities.get(brief.toString()).toJS();
        if (object) {
          object.user = userEntities.get(object.user.toString()).toJS();
        }

        return object;
      })
      .toJS();

    return (
      <div>
        <Grid>
          <ProjectsHeader
            config={config}
            filter={filter}
            paid={paid}
            getSelectedRoleName={this.getSelectedRoleName}
            loggedIn={user ? true : false}
          />

          <Row>
            <Col sm={8} md={9}>
              <BriefFilter
                user={user}
                paid={paid}
                filter={filter}
                onFiltersReset={this.onFiltersReset}
                onStatusFilterToggle={this.onStatusFilterToggle}
                onRemoteFilterToggle={this.onRemoteFilterToggle}
                onEditButton={() => this.setState({ showSignUpModal: true })}
                onInternalFilterToggle={this.onInternalFilterToggle}
                roleSkills={this.getFilterRoleSkills().toJS()}
                loadingRoleSkills={projects.getIn(
                  ['filterRoleSkills', paid.toString(), 'loading'],
                  null
                )}
                onSelectedRoleChange={this.onSelectedRoleChange}
                onExperienceLevelToggle={this.onExperienceLevelToggle}
                onHiringCapacityToggle={this.onHiringCapacityToggle}
                location={location}
                onLocationTextChange={this.onLocationTextChange}
                onSearchTermChange={this.onSearchTermChange}
                suggestedRoles={this.getSuggestedRoles().toJS()}
                selectedRole={this.getCurrentSelectedRole()}
                onClickRole={this.handleRoleFilterChange}
                loadingRoles={suggestedRoles.get('loading', false)}
                filterList={this.getFilterList()}
                onRolesSelect={this.onRolesSelect}
                onRolesHaveChanged={this.onRolesHaveChanged}
              />
              <BriefListings
                briefs={briefListings}
                id="brief-listings"
                loaded={projects.getIn(
                  ['briefs', this.getHash(), 'loaded'],
                  false
                )}
                loading={projects.getIn(
                  ['briefs', this.getHash(), 'loading'],
                  false
                )}
                selectedRole={this.getCurrentSelectedRole()}
                paid={paid}
                onClickRole={this.handleRoleFilterChange}
                onClickLocation={this.onLocationTextChange}
                country={filter && filter.country ? filter.country : ''}
                city={filter && filter.city ? filter.city : ''}
                partners={partners}
                loadedPartners={loadedPartners}
                loadingPartners={loadingPartners}
                onShowSignUpModal={() =>
                  this.setState({ showSignUpModal: true })
                }
              />
            </Col>
            <Col sm={4} md={3}>
              {showUpsell && <UpsellSignUp user={user} />}
              {!user && <AriUpsell />}
              <ApplicationWriterUpsell user={user} />
              {!user && (
                <JobsList
                  cityList={cities}
                  country={filter.country}
                  city={filter.city}
                  selectedRole={this.getSelectedRoleName()}
                />
              )}
              <PostJobUpsell
                role={this.getSelectedRoleName()}
                country={filter && filter.country ? filter.country : ''}
                city={filter && filter.city ? filter.city : ''}
              />
            </Col>
          </Row>
        </Grid>

        {!user && (
          <Modal
            show={showSignUpModal}
            subtitle="Sign-up now to get jobs straight to your inbox"
            closeButton
            onExit={() => {
              this.setState({
                showSignUpModal: false,
              });
            }}
          >
            <SignUp
              redirect={'onboarding'}
              redirectFromGoogle={'signup'}
              defaultAccountType={'seller'}
            />
          </Modal>
        )}
      </div>
    );
  }
}
